import { getDefaultStore } from "jotai";
import { selectedTpidAtom } from "../atoms";
import { API_ENDPOINTS } from "./apiEndpoints";

// TYPES

export interface TimeTablePeriod {
  Id?: number;
  id?: number;
  From?: string;
  from?: string;
  To?: string;
  to?: string;
  Label?: string;
  label?: string;
}

export type NormalizedPeriod = {
  id: number | null;
  from: Date | null;
  to: Date | null;
  label: string | null;
};

export type GeoServerLayers = {
  Stops: string;
  StopPoint: string;
  SalesPoints: string;
  Lines: string;
  TaxeZones: string;
  PayZones: string;
  Naromraden: string;
  TimeTablePeriods: string;
  CommuterParkings: string;
};

type GeoServerTimeTablePeriodProperties = {
  tpid?: number | string;
  datefrom?: string;
  dateto?: string;
  tptext?: string;
};

type GeoServerTimeTablePeriodFeature = {
  type?: string;
  id?: string;
  geometry?: unknown;
  properties?: GeoServerTimeTablePeriodProperties;
};

type GeoServerTimeTablePeriodResponse = {
  type?: string;
  features?: GeoServerTimeTablePeriodFeature[];
  totalFeatures?: number;
  numberMatched?: number;
  numberReturned?: number;
};

// STATIC CONFIG

const GEO_LAYERS: GeoServerLayers = {
  Stops: "ogt:hpl3",
  StopPoint: "ogt:hpll3",
  SalesPoints: "ogt:forsaljningsstallen",
  Lines: "ogt:linjer3",
  TaxeZones: "ogt:zoner",
  PayZones: "ogt:nyazoner",
  Naromraden: "ogt:naromraden",
  TimeTablePeriods: "ogt:tp",
  CommuterParkings: "ogt:forsaljningsstallen",
};

export async function getFeatureTypeName<K extends keyof GeoServerLayers>(
  key: K
): Promise<GeoServerLayers[K]> {
  return GEO_LAYERS[key];
}

export async function loadGeoServerFeatureIds(): Promise<GeoServerLayers> {
  return GEO_LAYERS;
}

// PERIOD HELPERS

function toDate(s?: string) {
  if (!s) return null;

  const d = new Date(s);

  return Number.isNaN(+d) ? null : d;
}

export function normalizePeriod(p: TimeTablePeriod): NormalizedPeriod {
  const id = p.Id ?? p.id ?? null;
  const from = toDate(p.From ?? p.from);
  const to = toDate(p.To ?? p.to);
  const label = p.Label ?? p.label ?? null;

  return {
    id,
    from,
    to,
    label,
  };
}

export function pickActivePeriod(
  periods: TimeTablePeriod[],
  now = new Date()
): NormalizedPeriod | null {
  for (const p of periods) {
    const n = normalizePeriod(p);

    if (n.from && n.to && now >= n.from && now < n.to) {
      return n;
    }
  }

  return null;
}

function pickLatestPeriod(
  periods: TimeTablePeriod[],
  now = new Date()
): NormalizedPeriod | null {
  const normalized = periods
    .map(normalizePeriod)
    .filter(
      (period) => period.id != null && period.from != null && period.from <= now
    );

  if (!normalized.length) {
    return null;
  }

  normalized.sort((a, b) => {
    const aDate = a.to?.getTime() ?? a.from?.getTime() ?? 0;
    const bDate = b.to?.getTime() ?? b.from?.getTime() ?? 0;

    return bDate - aDate;
  });

  return normalized[0] ?? null;
}

// API

export async function loadTimeTablePeriods(): Promise<TimeTablePeriod[]> {
  const res = await fetch(API_ENDPOINTS.GEO_TIMETABLE_PERIODS);

  if (!res.ok) {
    throw new Error(
      `Kunde inte hämta timetable periods: ${res.status} ${res.statusText}`
    );
  }

  const data: unknown = await res.json();

  if (!Array.isArray(data)) {
    throw new Error("Ogiltigt svar från Geo/TimetablePeriods");
  }

  return data as TimeTablePeriod[];
}

export async function getActivePeriod(): Promise<NormalizedPeriod | null> {
  const periods = await loadTimeTablePeriods();

  return pickActivePeriod(periods);
}

// GEOSERVER TIMETABLE PERIOD FALLBACK

async function loadGeoServerTimeTablePeriods(): Promise<TimeTablePeriod[]> {
  const typeName = await getFeatureTypeName("TimeTablePeriods");

  const xml = `
<GetFeature service="WFS" version="1.0.0" outputFormat="application/json"
  xmlns:wfs="http://www.opengis.net/wfs"
  xmlns:ogc="http://www.opengis.net/ogc"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-basic.xsd">
  <wfs:Query typeName="${typeName}">
    <wfs:PropertyName>tpid</wfs:PropertyName>
    <wfs:PropertyName>datefrom</wfs:PropertyName>
    <wfs:PropertyName>dateto</wfs:PropertyName>
    <wfs:PropertyName>tptext</wfs:PropertyName>
  </wfs:Query>
</GetFeature>`.trim();

  const res = await fetch(API_ENDPOINTS.GEO_FETCH, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
    },
    body: `=${encodeURIComponent(xml)}`,
  });

  if (!res.ok) {
    throw new Error(
      `Kunde inte hämta timetable periods från GeoServer: ${res.status} ${res.statusText}`
    );
  }

  const data = (await res.json()) as GeoServerTimeTablePeriodResponse;

  const features = data.features ?? [];

  return features
    .map((feature): TimeTablePeriod | null => {
      const properties = feature.properties;

      if (!properties) {
        return null;
      }

      const id = Number(properties.tpid);

      if (!Number.isFinite(id)) {
        return null;
      }

      return {
        Id: id,
        From: properties.datefrom,
        To: properties.dateto,
        Label: properties.tptext?.trim(),
      };
    })
    .filter((period): period is TimeTablePeriod => period !== null);
}

// TPID

export async function getActiveTpid(): Promise<number | null> {
  try {
    const store = getDefaultStore();
    const override = store.get(selectedTpidAtom);

    if (override != null) {
      return override;
    }
  } catch {}

  try {
    const activePeriod = await getActivePeriod();

    if (activePeriod?.id != null) {
      return activePeriod.id;
    }
  } catch (error) {
    console.warn("Kunde inte hämta aktiv timetable period från API:", error);
  }

  try {
    const geoServerPeriods = await loadGeoServerTimeTablePeriods();

    const activeGeoServerPeriod = pickActivePeriod(geoServerPeriods);

    if (activeGeoServerPeriod?.id != null) {
      return activeGeoServerPeriod.id;
    }

    const latestPeriod = pickLatestPeriod(geoServerPeriods);

    if (latestPeriod?.id != null) {
      console.warn(
        `Ingen aktiv tidtabellsperiod hittades. Använder senaste kända TPID ${latestPeriod.id} som fallback.`
      );

      return latestPeriod.id;
    }
  } catch (error) {
    console.error("Kunde inte hämta timetable periods från GeoServer:", error);
  }

  return null;
}

export function setSelectedTpid(tpid: number | null) {
  const store = getDefaultStore();
  store.set(selectedTpidAtom, tpid);
}

export function clearSelectedTpid() {
  const store = getDefaultStore();
  store.set(selectedTpidAtom, null);
}

// FORMAT

export function formatDateRange(
  from: Date | null,
  to: Date | null,
  locale = "sv-SE"
) {
  const fmt: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };

  const fromStr = from
    ? new Intl.DateTimeFormat(locale, fmt).format(from)
    : "N/A";

  const toStr = to ? new Intl.DateTimeFormat(locale, fmt).format(to) : "N/A";

  return { fromStr, toStr };
}
